import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  keyframes,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import NorthEastIcon from '@mui/icons-material/NorthEast';

// =====================================================
// AboutPage — Editorial Light
// 设计原则：
//   1. 白底 + 浅蓝品牌色（与首页一致），但极度克制
//   2. 编辑风 (editorial) 版式：左对齐、章节编号、巨量留白
//   3. 衬线字体做主标题（系统 serif 栈，无需额外加载）
//   4. 单一强调色：浅蓝 #2563eb，只用在链接箭头和章节号
//   5. 不用：浮动蓝云、玻璃卡片、彩虹渐变文字、居中堆叠
// =====================================================

const SERIF =
  '"Iowan Old Style", "Apple Garamond", "Baskerville", "Times New Roman", "Songti SC", "宋体", Georgia, serif';

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const shimmerBorder = keyframes`
  0%   { background-position: 0% 50%, 0% 50%; }
  100% { background-position: 200% 50%, 200% 50%; }
`;

// ---------- 数据 ----------
const principles = [
  {
    n: '01',
    t: '国内直连',
    s: 'No VPN. No foreign cards.',
    d: '注册登录就能用上 OpenAI、Google、xAI 的最新模型。完全合规，稳定低延迟，不需要任何额外的工具与账号。',
  },
  {
    n: '02',
    t: '一套积分',
    s: 'One wallet, every model.',
    d: '不用每家单独开会员，不用算汇率。一份积分跑遍 Veo、Sora、Grok、Nano Banana 全部模型，按次结算，一目了然。',
  },
  {
    n: '03',
    t: '按次付费',
    s: 'Pay only when you create.',
    d: '没有订阅陷阱，没有自动续费。不生成不扣费，积分永不过期。偶尔用一次和天天用一样划算。',
  },
];

const models = [
  { name: 'Sora 2', by: 'OpenAI', desc: '会拍电影的 AI，无水印、可商用' },
  { name: 'Veo 3 Pro', by: 'Google DeepMind', desc: '广告级画面，电影感配色' },
  { name: 'Veo 3 / Veo 3.1', by: 'Google DeepMind', desc: '日常创作首选，速度与画质平衡' },
  { name: 'Grok Imagine', by: 'xAI', desc: '10s 快速 / 30s 长镜头，最有想象力' },
  { name: 'Nano Banana 2', by: 'Google', desc: '1K 高清文生图与图生图' },
];

const faqs = [
  {
    q: 'Sora 2 国内真的可以直接用吗？',
    a: '是的。Xup 已接入 OpenAI Sora 2 官方能力，注册登录即可在浏览器里使用，生成的视频无水印、可商用。',
  },
  {
    q: 'Veo 3 和 Veo 3 Pro 有什么区别？',
    a: 'Veo 3 适合日常创作，速度快、性价比高；Veo 3 Pro 输出更高清、细节更锐利，适合商业片、广告与正式项目。Xup 都已接入，一键切换。',
  },
  {
    q: 'Grok Imagine 支持多长的视频？',
    a: 'Grok 有两种模式：10 秒快速模式（grok-video-3-10s）与 Imagine 长镜头模式（最长 30 秒，可自定义时长）。',
  },
  {
    q: '一次生成大概花多少积分？',
    a: '图片最低 1 积分起，视频按模型与时长计费。每次点击生成前，按钮上都会清楚显示本次消耗的积分，所见即所得。',
  },
  {
    q: '生成的作品有版权吗？',
    a: '按各模型服务条款，通过 Xup 生成的内容归用户所有，可用于个人创作与商业用途。详见服务条款。',
  },
];

// ---------- 章节标题（左侧编号 + 右侧标题）----------
const SectionHeader: React.FC<{ num: string; title: string; subtitle?: string }> = ({
  num,
  title,
  subtitle,
}) => (
  <Box sx={{ display: 'flex', gap: { xs: 3, md: 8 }, mb: { xs: 5, md: 8 }, alignItems: 'baseline' }}>
    <Typography
      sx={{
        fontSize: { xs: '0.85rem', md: '1rem' },
        fontWeight: 600,
        color: '#2563eb',
        fontFamily: '"Orbitron", sans-serif',
        letterSpacing: '2px',
        flexShrink: 0,
        pt: { md: 1 },
      }}
    >
      {num}
    </Typography>
    <Box>
      <Typography
        component="h2"
        sx={{
          fontFamily: SERIF,
          fontSize: { xs: '2rem', md: '3.2rem' },
          fontWeight: 400,
          fontStyle: 'italic',
          color: '#0a0a0a',
          letterSpacing: '-0.015em',
          lineHeight: 1.1,
          mb: subtitle ? 2 : 0,
        }}
      >
        {title}
      </Typography>
      {subtitle && (
        <Typography
          sx={{
            fontSize: { xs: '1rem', md: '1.15rem' },
            color: '#737373',
            lineHeight: 1.6,
            maxWidth: 580,
            fontWeight: 400,
          }}
        >
          {subtitle}
        </Typography>
      )}
    </Box>
  </Box>
);

// ---------- 主组件 ----------
const AboutPage: React.FC = () => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState<string | false>('faq-0');

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#ffffff',
        color: '#0a0a0a',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* 极轻量背景：仅页面顶部一抹浅蓝，无浮动云 */}
      <Box
        sx={{
          position: 'absolute',
          top: -300,
          right: -200,
          width: 900,
          height: 600,
          background:
            'radial-gradient(ellipse 60% 70% at 50% 50%, rgba(191,219,254,0.45) 0%, rgba(255,255,255,0) 70%)',
          filter: 'blur(40px)',
          pointerEvents: 'none',
          zIndex: 0,
        }}
      />

      {/* ========== 顶部导航（与 LandingPage 一致） ========== */}
      <Box
        component="nav"
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: { xs: '20px 24px', md: '28px 56px' },
          position: 'relative',
          zIndex: 10,
        }}
      >
        <Box
          onClick={() => navigate('/')}
          sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 900,
              color: '#000000',
              letterSpacing: '2px',
              fontSize: { xs: '20px', md: '24px' },
              fontFamily: '"Orbitron", sans-serif',
            }}
          >
            Xup
          </Typography>
        </Box>

        <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 6, alignItems: 'center' }}>
          <Typography
            onClick={() => navigate('/')}
            sx={{
              color: '#1f2937',
              cursor: 'pointer',
              fontSize: '15px',
              fontWeight: 500,
              '&:hover': { color: '#2563eb' },
              transition: 'color 0.2s',
            }}
          >
            首页
          </Typography>
          <Typography
            sx={{
              color: '#2563eb',
              fontSize: '15px',
              fontWeight: 700,
              cursor: 'default',
            }}
          >
            关于 Xup
          </Typography>
          <Typography
            onClick={() => navigate('/login')}
            sx={{
              color: '#1f2937',
              cursor: 'pointer',
              fontSize: '15px',
              fontWeight: 400,
              '&:hover': { color: '#000000' },
              transition: 'color 0.2s',
            }}
          >
            登录
          </Typography>
          <Button
            onClick={() => navigate('/login')}
            variant="contained"
            sx={{
              background:
                'linear-gradient(135deg, #3b82f6, #2563eb) padding-box, linear-gradient(90deg, #FF0080, #00FFFF, #00FF00, #FFD700, #FF0080) border-box',
              border: '3px solid transparent',
              color: 'white',
              borderRadius: '100px',
              padding: '3px 24px',
              textTransform: 'none',
              fontSize: '15px',
              fontWeight: 600,
              boxShadow: '0 4px 12px rgba(37, 99, 235, 0.2)',
              backgroundSize: '200% 100%',
              backgroundPosition: '0% 50%',
              transition: 'background-position 2s linear, box-shadow 0.3s ease',
              '&:hover': {
                background:
                  'linear-gradient(135deg, #2563eb, #1d4ed8) padding-box, linear-gradient(90deg, #FF0080, #00FFFF, #00FF00, #FFD700, #FF0080) border-box',
                backgroundSize: '200% 100%',
                boxShadow: '0 8px 24px rgba(37, 99, 235, 0.4)',
                backgroundPosition: '100% 50%',
              },
            }}
          >
            立即开始创作
          </Button>
        </Box>

        <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
          <Button
            onClick={() => navigate('/login')}
            variant="contained"
            size="small"
            sx={{
              bgcolor: '#2563eb',
              borderRadius: '100px',
              textTransform: 'none',
              fontSize: '13px',
              fontWeight: 600,
              px: 2.5,
              '&:hover': { bgcolor: '#1d4ed8' },
            }}
          >
            登录
          </Button>
        </Box>
      </Box>

      {/* ========== Hero — 编辑风：左对齐、衬线大标、署名样元数据 ========== */}
      <Container
        maxWidth="lg"
        sx={{
          pt: { xs: 8, md: 16 },
          pb: { xs: 10, md: 18 },
          position: 'relative',
          zIndex: 1,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            mb: { xs: 6, md: 10 },
            opacity: 0,
            animation: `${fadeUp} 0.8s ease-out forwards`,
          }}
        >
          <Typography
            sx={{
              fontSize: '0.78rem',
              fontWeight: 600,
              color: '#737373',
              fontFamily: '"Orbitron", sans-serif',
              letterSpacing: '2.5px',
            }}
          >
            A&nbsp;LETTER&nbsp;FROM&nbsp;XUP
          </Typography>
          <Typography
            sx={{
              fontSize: '0.78rem',
              fontWeight: 600,
              color: '#737373',
              fontFamily: '"Orbitron", sans-serif',
              letterSpacing: '2.5px',
              display: { xs: 'none', sm: 'block' },
            }}
          >
            EST.&nbsp;2025
          </Typography>
        </Box>

        <Box
          sx={{
            maxWidth: 1100,
            opacity: 0,
            animation: `${fadeUp} 0.9s 0.15s ease-out forwards`,
          }}
        >
          <Typography
            component="h1"
            sx={{
              fontFamily: SERIF,
              fontSize: { xs: '2.8rem', sm: '4rem', md: '6.4rem' },
              fontWeight: 400,
              lineHeight: { xs: 1.1, md: 1.02 },
              letterSpacing: '-0.025em',
              color: '#0a0a0a',
              mb: { xs: 5, md: 7 },
            }}
          >
            AI&nbsp;不该
            <br />
            只属于
            <Box
              component="span"
              sx={{
                fontStyle: 'italic',
                color: '#2563eb',
                fontWeight: 400,
              }}
            >
              {' '}工程师
            </Box>
            <Box component="span" sx={{ color: '#cbd5e1' }}>.</Box>
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '120px 1fr' },
              gap: { xs: 3, md: 8 },
              maxWidth: 880,
              mb: { xs: 6, md: 8 },
            }}
          >
            <Typography
              sx={{
                fontSize: '0.78rem',
                color: '#737373',
                fontWeight: 600,
                letterSpacing: '1.5px',
                fontFamily: '"Orbitron", sans-serif',
                pt: 1,
              }}
            >
              PROLOGUE
            </Typography>
            <Box>
              <Typography
                sx={{
                  fontSize: { xs: '1.05rem', md: '1.25rem' },
                  color: '#404040',
                  lineHeight: 1.75,
                  fontWeight: 400,
                  mb: 3,
                }}
              >
                每一周都有新模型上线，能拍出几年前要花几十万才能完成的镜头。但每一周，
                这种能力都被锁在&nbsp;VPN、海外信用卡、复杂订阅之外——只有少数人能用上。
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: '1.05rem', md: '1.25rem' },
                  color: '#404040',
                  lineHeight: 1.75,
                  fontWeight: 400,
                }}
              >
                我们做&nbsp;Xup，是想让这件事简单到&nbsp;
                <Box component="span" sx={{ color: '#0a0a0a', fontWeight: 600 }}>
                  注册即用
                </Box>
                。把&nbsp;Sora&nbsp;2、Veo&nbsp;3、Grok&nbsp;Imagine、Nano&nbsp;Banana&nbsp;2
                这些原本互不相通的世界级模型，放进同一扇门，按次结算，
                让一个有想法的普通人，也能一句话拍一部短片。
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', flexWrap: 'wrap' }}>
            <Button
              onClick={() => navigate('/home')}
              variant="contained"
              endIcon={<ArrowForwardIcon />}
              sx={{
                px: 4,
                py: 1.5,
                borderRadius: '100px',
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 600,
                background:
                  'linear-gradient(135deg, #3b82f6, #2563eb) padding-box, linear-gradient(90deg, #FF0080, #00FFFF, #00FF00, #FFD700, #FF0080) border-box',
                backgroundSize: '200% 100%, 200% 100%',
                border: '3px solid transparent',
                color: 'white',
                boxShadow: '0 10px 30px -10px rgba(37,99,235,0.5)',
                animation: `${shimmerBorder} 6s linear infinite`,
                '&:hover': {
                  boxShadow: '0 14px 36px -10px rgba(37,99,235,0.65)',
                  transform: 'translateY(-1px)',
                },
                transition: 'transform 0.25s ease, box-shadow 0.25s ease',
              }}
            >
              开始创作
            </Button>
            <Box
              onClick={() => navigate('/pricing')}
              sx={{
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                color: '#0a0a0a',
                fontSize: '0.95rem',
                fontWeight: 500,
                borderBottom: '1px solid #0a0a0a',
                pb: 0.4,
                transition: 'color 0.2s, border-color 0.2s',
                '&:hover': { color: '#2563eb', borderColor: '#2563eb' },
              }}
            >
              查看积分套餐
            </Box>
          </Box>
        </Box>
      </Container>

      {/* ========== § 01 信念 — 编辑风列表，无卡片 ========== */}
      <Box sx={{ borderTop: '1px solid #e5e5e5', position: 'relative', zIndex: 1 }}>
        <Container maxWidth="lg" sx={{ py: { xs: 10, md: 16 } }}>
          <SectionHeader
            num="§ 01"
            title="三件不会变的事。"
            subtitle="产品会迭代，模型会更新，但这三条，从第一天到现在，没改过。"
          />

          <Box sx={{ maxWidth: 980 }}>
            {principles.map((p, i) => (
              <Box
                key={p.n}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: '120px 1fr 2fr' },
                  gap: { xs: 2, md: 8 },
                  py: { xs: 5, md: 7 },
                  borderTop: i === 0 ? 'none' : '1px solid #e5e5e5',
                }}
              >
                <Typography
                  sx={{
                    fontSize: { xs: '1rem', md: '1.1rem' },
                    fontWeight: 600,
                    color: '#2563eb',
                    fontFamily: '"Orbitron", sans-serif',
                    letterSpacing: '1.5px',
                  }}
                >
                  {p.n}
                </Typography>

                <Box>
                  <Typography
                    component="h3"
                    sx={{
                      fontFamily: SERIF,
                      fontSize: { xs: '1.7rem', md: '2.2rem' },
                      fontWeight: 400,
                      fontStyle: 'italic',
                      color: '#0a0a0a',
                      letterSpacing: '-0.015em',
                      lineHeight: 1.1,
                      mb: 1.2,
                    }}
                  >
                    {p.t}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '0.82rem',
                      color: '#737373',
                      fontFamily: '"Orbitron", sans-serif',
                      letterSpacing: '1.5px',
                    }}
                  >
                    {p.s}
                  </Typography>
                </Box>

                <Typography
                  sx={{
                    fontSize: { xs: '1rem', md: '1.1rem' },
                    color: '#404040',
                    lineHeight: 1.8,
                  }}
                >
                  {p.d}
                </Typography>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* ========== § 02 模型 — 演职员表风格 ========== */}
      <Box sx={{ borderTop: '1px solid #e5e5e5', position: 'relative', zIndex: 1 }}>
        <Container maxWidth="lg" sx={{ py: { xs: 10, md: 16 } }}>
          <SectionHeader
            num="§ 02"
            title="我们与谁同行。"
            subtitle="直接对接 OpenAI、Google DeepMind、xAI 官方能力，确保你用到的永远是最新版本。"
          />

          <Box sx={{ maxWidth: 980 }}>
            {models.map((m, i) => (
              <Box
                key={m.name}
                onClick={() => navigate('/home')}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: '40px 2fr 1.6fr 2fr 32px' },
                  gap: { xs: 1, md: 4 },
                  alignItems: 'baseline',
                  py: { xs: 3.5, md: 4 },
                  borderTop: i === 0 ? '1px solid #e5e5e5' : 'none',
                  borderBottom: '1px solid #e5e5e5',
                  cursor: 'pointer',
                  transition: 'all 0.3s cubic-bezier(0.4,0,0.2,1)',
                  '&:hover': {
                    bgcolor: 'rgba(239,246,255,0.6)',
                    px: { md: 2 },
                    '& .model-arrow': {
                      transform: 'translate(4px, -4px)',
                      color: '#2563eb',
                    },
                    '& .model-name': {
                      color: '#2563eb',
                    },
                  },
                }}
              >
                <Typography
                  sx={{
                    fontSize: '0.85rem',
                    fontWeight: 600,
                    color: '#a3a3a3',
                    fontFamily: '"Orbitron", sans-serif',
                    letterSpacing: '1px',
                  }}
                >
                  {String(i + 1).padStart(2, '0')}
                </Typography>
                <Typography
                  className="model-name"
                  sx={{
                    fontFamily: SERIF,
                    fontSize: { xs: '1.6rem', md: '2rem' },
                    fontWeight: 400,
                    color: '#0a0a0a',
                    letterSpacing: '-0.02em',
                    lineHeight: 1.1,
                    transition: 'color 0.3s',
                  }}
                >
                  {m.name}
                </Typography>
                <Typography
                  sx={{
                    fontSize: '0.82rem',
                    color: '#737373',
                    fontFamily: '"Orbitron", sans-serif',
                    letterSpacing: '1.5px',
                  }}
                >
                  {m.by}
                </Typography>
                <Typography
                  sx={{
                    fontSize: { xs: '0.95rem', md: '1.05rem' },
                    color: '#404040',
                    lineHeight: 1.6,
                  }}
                >
                  {m.desc}
                </Typography>
                <NorthEastIcon
                  className="model-arrow"
                  sx={{
                    fontSize: 22,
                    color: '#cbd5e1',
                    transition: 'all 0.3s cubic-bezier(0.4,0,0.2,1)',
                    justifySelf: 'end',
                    display: { xs: 'none', md: 'inline' },
                  }}
                />
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* ========== § 03 FAQ — 纯排版 + 细线 ========== */}
      <Box sx={{ borderTop: '1px solid #e5e5e5', position: 'relative', zIndex: 1 }}>
        <Container maxWidth="lg" sx={{ py: { xs: 10, md: 16 } }}>
          <SectionHeader
            num="§ 03"
            title="你可能想问的。"
          />

          <Box sx={{ maxWidth: 880 }}>
            {faqs.map((item, i) => {
              const id = `faq-${i}`;
              const isOpen = expanded === id;
              return (
                <Accordion
                  key={id}
                  expanded={isOpen}
                  onChange={(_, isExp) => setExpanded(isExp ? id : false)}
                  elevation={0}
                  disableGutters
                  square
                  sx={{
                    bgcolor: 'transparent',
                    borderTop: i === 0 ? '1px solid #e5e5e5' : 'none',
                    borderBottom: '1px solid #e5e5e5',
                    '&:before': { display: 'none' },
                  }}
                >
                  <AccordionSummary
                    expandIcon={
                      <ExpandMoreIcon
                        sx={{
                          color: isOpen ? '#2563eb' : '#a3a3a3',
                          transition: 'color 0.3s',
                          fontSize: 26,
                        }}
                      />
                    }
                    sx={{
                      px: 0,
                      py: 1,
                      '& .MuiAccordionSummary-content': { my: { xs: 2.5, md: 3 } },
                    }}
                  >
                    <Box sx={{ display: 'flex', gap: { xs: 2, md: 4 }, alignItems: 'baseline' }}>
                      <Typography
                        sx={{
                          fontSize: '0.85rem',
                          fontWeight: 600,
                          color: '#a3a3a3',
                          fontFamily: '"Orbitron", sans-serif',
                          letterSpacing: '1px',
                          flexShrink: 0,
                          minWidth: { md: 32 },
                        }}
                      >
                        {String(i + 1).padStart(2, '0')}
                      </Typography>
                      <Typography
                        component="h3"
                        sx={{
                          fontFamily: SERIF,
                          fontSize: { xs: '1.15rem', md: '1.45rem' },
                          fontWeight: 400,
                          color: '#0a0a0a',
                          letterSpacing: '-0.01em',
                          lineHeight: 1.3,
                          transition: 'color 0.3s',
                        }}
                      >
                        {item.q}
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ px: 0, pb: 4, pt: 0 }}>
                    <Box sx={{ display: 'flex', gap: { xs: 2, md: 4 } }}>
                      <Box sx={{ minWidth: { xs: 0, md: 32 }, flexShrink: 0 }} />
                      <Typography
                        sx={{
                          fontSize: { xs: '0.98rem', md: '1.05rem' },
                          color: '#525252',
                          lineHeight: 1.85,
                          maxWidth: 720,
                        }}
                      >
                        {item.a}
                      </Typography>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Box>
        </Container>
      </Box>

      {/* ========== § 04 收尾 — 一封信的署名 ========== */}
      <Box sx={{ borderTop: '1px solid #e5e5e5', position: 'relative', zIndex: 1 }}>
        <Container maxWidth="lg" sx={{ py: { xs: 12, md: 20 } }}>
          <Box sx={{ maxWidth: 880 }}>
            <Typography
              sx={{
                fontSize: '0.78rem',
                fontWeight: 600,
                color: '#2563eb',
                fontFamily: '"Orbitron", sans-serif',
                letterSpacing: '2.5px',
                mb: { xs: 4, md: 5 },
              }}
            >
              §&nbsp;04&nbsp;·&nbsp;BEGIN
            </Typography>

            <Typography
              component="h2"
              sx={{
                fontFamily: SERIF,
                fontSize: { xs: '2.4rem', md: '4.4rem' },
                fontWeight: 400,
                lineHeight: 1.05,
                letterSpacing: '-0.025em',
                color: '#0a0a0a',
                mb: { xs: 4, md: 6 },
              }}
            >
              你的第一部&nbsp;AI&nbsp;短片，
              <br />
              <Box component="span" sx={{ fontStyle: 'italic', color: '#2563eb' }}>
                从这里开始
              </Box>
              <Box component="span" sx={{ color: '#cbd5e1' }}>.</Box>
            </Typography>

            <Typography
              sx={{
                fontSize: { xs: '1.05rem', md: '1.2rem' },
                color: '#525252',
                lineHeight: 1.75,
                mb: { xs: 6, md: 8 },
                maxWidth: 620,
              }}
            >
              一句话、一张图，就够了。注册不要钱，赠送的积分够你拍完第一条片子。
            </Typography>

            <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', flexWrap: 'wrap', mb: { xs: 8, md: 10 } }}>
              <Button
                onClick={() => navigate('/home')}
                variant="contained"
                endIcon={<ArrowForwardIcon />}
                sx={{
                  px: 4,
                  py: 1.5,
                  borderRadius: '100px',
                  textTransform: 'none',
                  fontSize: '1rem',
                  fontWeight: 600,
                  background:
                    'linear-gradient(135deg, #3b82f6, #2563eb) padding-box, linear-gradient(90deg, #FF0080, #00FFFF, #00FF00, #FFD700, #FF0080) border-box',
                  backgroundSize: '200% 100%, 200% 100%',
                  border: '3px solid transparent',
                  color: 'white',
                  boxShadow: '0 12px 32px -10px rgba(37,99,235,0.5)',
                  animation: `${shimmerBorder} 6s linear infinite`,
                  '&:hover': {
                    boxShadow: '0 16px 40px -10px rgba(37,99,235,0.65)',
                    transform: 'translateY(-1px)',
                  },
                  transition: 'transform 0.25s ease, box-shadow 0.25s ease',
                }}
              >
                立即开始创作
              </Button>
              <Box
                onClick={() => navigate('/pricing')}
                sx={{
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 1,
                  color: '#0a0a0a',
                  fontSize: '0.95rem',
                  fontWeight: 500,
                  borderBottom: '1px solid #0a0a0a',
                  pb: 0.4,
                  transition: 'color 0.2s, border-color 0.2s',
                  '&:hover': { color: '#2563eb', borderColor: '#2563eb' },
                }}
              >
                了解套餐
              </Box>
            </Box>

            {/* 署名 */}
            <Box sx={{ pt: { xs: 4, md: 6 }, borderTop: '1px solid #e5e5e5' }}>
              <Typography
                sx={{
                  fontFamily: SERIF,
                  fontSize: { xs: '1.1rem', md: '1.3rem' },
                  fontStyle: 'italic',
                  color: '#0a0a0a',
                  fontWeight: 400,
                  mb: 0.8,
                }}
              >
                — Xup&nbsp;Team
              </Typography>
              <Typography
                sx={{
                  fontSize: '0.82rem',
                  color: '#737373',
                  fontFamily: '"Orbitron", sans-serif',
                  letterSpacing: '1.5px',
                }}
              >
                XUP.DIGITAL&nbsp;·&nbsp;2026
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* 页脚 */}
      <Box
        sx={{
          textAlign: 'center',
          py: 4,
          borderTop: '1px solid #e5e5e5',
          position: 'relative',
          zIndex: 1,
        }}
      >
        <Typography
          sx={{
            fontSize: '0.78rem',
            color: '#a3a3a3',
            fontWeight: 500,
            letterSpacing: '0.5px',
          }}
        >
          © {new Date().getFullYear()}&nbsp;Xup&nbsp;·&nbsp;让 AI 走进普通人的生活
        </Typography>
      </Box>
    </Box>
  );
};

export default AboutPage;
